import { useMemo } from "react";
import TableComponentV2 from "../../../../common/components/TableComponentV2";
import { useFreshHourlyOOSData } from "../hooks";
import LoadingComponent from "../../../../common/components/LoadingComponent";
// import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import moment from "moment";
import { Box, Tooltip, Typography } from "@mui/material";

const thickBorder = '3px solid #C3C3C3!important'
const customCellStyle = {
    textAlign: "left",
    verticalAlign: "middle",
};


export default function FreshHourlyTable({ params }) {
    const { data, isLoading } = useFreshHourlyOOSData({ params });

    const HOURS = [
        "8:00 AM", "9:00 AM", "10:00 AM", "11:00 AM", "12:00 PM",
        "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM", "5:00 PM",
        "6:00 PM", "7:00 PM", "8:00 PM", "9:00 PM", "10:00 PM"
    ];
    const formatHourDisplay = (h) => moment(h, "h:mm A").format("H");

    const preprocessData = (rawData, hours, totalDays, endDate) => {
        return rawData.map((row) => {
            const newRow = { ...row, _displayedMap: {} };

            for (let i = 0; i < totalDays; i++) {
                const dateKey = moment(endDate).subtract(i, "day").format("YYYY-MM-DD");
                const dateData = row[dateKey] || {};
                newRow._displayedMap[dateKey] = {};

                const isEmpty = (v) =>
                    v === null || v === undefined || v === 0 || v === "" || v === "0";

                hours.forEach((h, hourIndex) => {
                    const hourKey = moment(h, "h:mm A").format("HH:mm");

                    // Next 2 hours keys in HH:mm format
                    const next1Key = hours[hourIndex + 1]
                        ? moment(hours[hourIndex + 1], "h:mm A").format("HH:mm")
                        : null;
                    const next2Key = hours[hourIndex + 2]
                        ? moment(hours[hourIndex + 2], "h:mm A").format("HH:mm")
                        : null;

                    // Previous hour key in HH:mm format
                    const prevHourKey = hourIndex > 0
                        ? moment(hours[hourIndex - 1], "h:mm A").format("HH:mm")
                        : null;

                    // Convert current hour to 24-hour number for range checks
                    const hour24 = parseInt(moment(h, "h:mm A").format("HH"), 10);

                    let displayedValue;

                    if (hour24 >= 8 && hour24 <= 20) {
                        // 8AM - 8PM logic
                        const next1 = next1Key ? dateData[next1Key] ?? null : null;
                        const next2 = next2Key ? dateData[next2Key] ?? null : null;

                        if (isEmpty(dateData[hourKey]) && isEmpty(next1) && isEmpty(next2)) {
                            displayedValue = "ClosedIcon";
                        } else if (
                            prevHourKey &&
                            newRow._displayedMap[dateKey][prevHourKey] === "ClosedIcon" &&
                            isEmpty(dateData[hourKey])
                        ) {
                            displayedValue = "ClosedIcon";
                        } else {
                            displayedValue = "Blank";
                        }
                    } else if (hour24 >= 21 && hour24 <= 22) {
                        // 9PM - 10PM logic: check 8PM - 10PM for the day
                        const hour8PM = "20:00";
                        const hour9PM = "21:00";
                        const hour10PM = "22:00";

                        const noSales8to10 =
                            isEmpty(dateData[hour8PM]) &&
                            isEmpty(dateData[hour9PM]) &&
                            isEmpty(dateData[hour10PM]);

                        if (noSales8to10) {
                            displayedValue = "ClosedIcon";
                        } else if (
                            prevHourKey &&
                            newRow._displayedMap[dateKey][prevHourKey] === "ClosedIcon" &&
                            isEmpty(dateData[hourKey])
                        ) {
                            displayedValue = "ClosedIcon";
                        } else {
                            displayedValue = "Blank";
                        }
                    } else {
                        // Default for any other hours
                        displayedValue = "Blank";
                    }

                    newRow._displayedMap[dateKey][hourKey] = displayedValue;
                });
            }

            return newRow;
        });
    };

    // const preprocessRangeData = (rawData, hours) => {
    //     return rawData.map(row => {
    //         const newRow = { ...row, _displayedMap: { hours: {} } };

    //         hours.forEach((h, hourIndex) => {
    //             const hourKey = moment(h, "h:mm A").format("HH:mm");
    //             const currVal = row[hourKey];

    //             const next1Key = hourIndex + 1 < hours.length
    //                 ? moment(hours[hourIndex + 1], "h:mm A").format("HH:mm")
    //                 : null;
    //             const next2Key = hourIndex + 2 < hours.length
    //                 ? moment(hours[hourIndex + 2], "h:mm A").format("HH:mm")
    //                 : null;

    //             const next1 = next1Key ? row[next1Key] : null;
    //             const next2 = next2Key ? row[next2Key] : null;

    //             const isEmpty = v => v === null || v === undefined || v === 0 || v === "" || v === "0";

    //             let displayedValue;
    //             if (isEmpty(currVal) && isEmpty(next1) && isEmpty(next2)) {
    //                 displayedValue = "ClosedIcon";
    //             } else {
    //                 const prevHourKey = hourIndex > 0
    //                     ? moment(hours[hourIndex - 1], "h:mm A").format("HH:mm")
    //                     : null;
    //                 if (prevHourKey && newRow._displayedMap.hours[prevHourKey] === "ClosedIcon" && isEmpty(currVal)) {
    //                     displayedValue = "ClosedIcon";
    //                 } else {
    //                     displayedValue = "Blank";
    //                 }
    //             }

    //             newRow._displayedMap.hours[hourKey] = displayedValue;
    //         });

    //         return newRow;
    //     });
    // };

    const totalDays = 3; // for 3-day case
    const processedData = useMemo(() => {
        if (params?.days === "3") {
            return preprocessData(data || [], HOURS, 3, params?.endDate);
            // } else {
            //     return preprocessRangeData(data || [], HOURS);
        }
    }, [data, HOURS, params?.days, params?.endDate]);

    const fresh_hourly_oos_columns = useMemo(() => {
        const baseColumns = [
            {
                id: "article_number",
                header: "Article No.",
                accessorKey: "article_number",
                cell: (row) => {
                    const value = row?.getValue()

                    return <Box>
                        <Typography>{value}</Typography>
                    </Box>
                },
                customStyle: {
                    textAlign: "center",
                    verticalAlign: "middle",
                    width: "7rem",
                    minWidth: "7rem",
                    maxWidth: "7rem",
                    margin: '2rem'
                },
                headerStyle: {
                    width: "7rem",
                    minWidth: "7rem",
                    maxWidth: "7rem",
                    paddingLeft: "3rem",
                }
            },

            {
                id: "article_description",
                header: "Article Description",
                accessorKey: "article_description",
                cell: (row) => {
                    const value = row?.getValue()

                    return <Box sx={{ width: { sm: '90%' }, marginX: 'auto', }}>
                        <Typography>{value}</Typography>
                    </Box>
                },

                customStyle: {
                    ...customCellStyle,
                    borderRight: thickBorder,
                    width: "28rem",
                    minWidth: "28rem",
                    maxWidth: "28rem",
                    marginLeft: '1rem',

                },
                headerStyle: {
                    borderRight: thickBorder,
                    width: "28rem",
                    minWidth: "28rem",
                    maxWidth: "28rem",
                    marginLeft: '1rem',
                }
            },

        ];

        // Case 1: If days === 3 → create 3 groups
        if (params?.days === "3") {
            const totalDays = 3;

            const dayColumns = Array.from({ length: totalDays }, (_, index) => {
                const dateKey = moment(params?.endDate)
                    .subtract(index, "day")
                    .format("YYYY-MM-DD");

                const dateDisplay = moment(params?.endDate)
                    .subtract(index, "day")
                    .format("MMMM DD, YYYY");

                const eightPMIndex = HOURS.indexOf("8:00 PM");

                // Helper to check if value is empty
                const isEmpty = (v) => {
                    if (v === null || v === undefined) return true;
                    if (typeof v === "string") {
                        const s = v.trim();
                        return s === "" || s === "0" || s === "0.0" || s === "0.00";
                    }
                    if (typeof v === "number") return v === 0 || Number.isNaN(v);
                    return false;
                };

                return {
                    header: dateDisplay,
                    id: `date_time_${dateKey}`,
                    columns: [
                        // OOS% COLUMN
                        {
                            id: `${dateKey}_oos_percent`,
                            header: "OOS%",
                            accessorFn: (row) => {
                                const displayedMap = row._displayedMap?.[dateKey] || {};
                                // Count hours that are NOT Blank
                                return Object.values(displayedMap).filter((v) => v !== "Blank").length;
                            },
                            cell: (info) => {
                                const oosCount = info.getValue();        // X
                                const totalHours = HOURS.length;         // 15
                                const pct = (oosCount / totalHours) * 100;
                                const formula = `${oosCount} / ${totalHours} * 100`;

                                const pctText = `${pct.toFixed(1)}%`;

                                const tooltipText = `No. of OOS: ${oosCount}\nNo. of Hours: ${totalHours}\nFormula: ${formula}\nResult = ${pctText}`;

                                return (
                                    <Tooltip title={<span style={{ whiteSpace: "pre-line" }}>{tooltipText}</span>} placement="top">
                                        <span>{pctText}</span>
                                    </Tooltip>
                                );
                            },
                            customStyle: { width: "6rem", textAlign: "center" },
                            headerStyle: { width: "6rem", textAlign: "center" },
                        },

                        // HOURLY COLUMNS
                        ...HOURS.map((h, hourIndex) => {
                            const hourKey = moment(h, "h:mm A").format("HH:mm");

                            return {
                                id: `${dateKey}_${hourKey}`,
                                header: formatHourDisplay(h),
                                accessorFn: (row) => {
                                    const dateData = row?.[dateKey];
                                    const value = dateData?.[hourKey] ?? null;

                                    // Store "__CLOSED__" marker for previous hour detection
                                    const prevHourKey = HOURS[hourIndex - 1]
                                        ? moment(HOURS[hourIndex - 1], "h:mm A").format("HH:mm")
                                        : null;

                                    if (!dateData) return value;

                                    return value;
                                },
                                cell: (info) => {
                                    const row = info.row.original;
                                    const hourKey = moment(h, "h:mm A").format("HH:mm");
                                    const displayedValue = row._displayedMap?.[dateKey]?.[hourKey] || "Blank";

                                    const value = row?.[dateKey]?.[hourKey] ?? null;
                                    const isEmpty = value === null || value === undefined || value === 0 || value === "" || value === "0";

                                    return (
                                        <Tooltip
                                            title={isEmpty ? "No Sales" : `Sales: ₱${value}`}
                                            placement="top"
                                        >
                                            <div style={{ display: "flex", justifyContent: "center" }}>
                                                {displayedValue === "ClosedIcon" ? (
                                                    <span style={{ fontSize: '10px', color: 'red', fontWeight: 'bolder' }}>&#10006;</span>
                                                ) : (
                                                    <div>&nbsp;</div>
                                                )}
                                            </div>
                                        </Tooltip>
                                    );
                                },

                                customStyle: {
                                    width: "3rem",
                                    minWidth: "3rem",
                                    maxWidth: "3rem",
                                    textAlign: "center",
                                    ...(h === "10:00 PM" && { borderRight: thickBorder }),
                                },

                                headerStyle: {
                                    width: "3rem",
                                    minWidth: "3rem",
                                    maxWidth: "3rem",
                                    textAlign: "center",
                                    ...(h === "10:00 PM" && { borderRight: thickBorder }),
                                },
                            };
                        }),
                    ],
                };
            });

            return [...baseColumns, ...dayColumns];
        }



        // Case 2: days = 7/30/ytd → single range
        // return [
        //     ...baseColumns,
        //     {
        //         header: `${moment(params?.startDate).format("MMMM DD, YYYY")} - ${moment(params?.endDate).format("MMMM DD, YYYY")}`,
        //         id: "date_time_range",
        //         customStyle: {
        //             borderRight: thickBorder,
        //         },
        //         headerStyle: {
        //             borderRight: thickBorder,
        //         },
        //         columns: [
        //             // OOS% column
        //             {
        //                 id: `range_oos_percent`,
        //                 header: "OOS%",
        //                 accessorFn: (row) => {
        //                     const startDate = new Date(params.startDate);
        //                     const endDate = new Date(params.endDate);
        //                     const diffTime = endDate - startDate;
        //                     const totalDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
        //                     const totalDaysperHour = totalDays * 15;
        //                     const displayedMap = row._displayedMap?.hours || {};
        //                     const totalOOS = Object.values(displayedMap).filter(v => v !== "Blank").length;
        //                     return totalOOS > 0 ? (totalOOS / totalDaysperHour) * 100 : 0;
        //                 },
        //                 cell: (info) => {
        //                     const pct = info.getValue();
        //                     const row = info.row.original;

        //                     // Compute totalDays again for tooltip
        //                     const startDate = new Date(params.startDate);
        //                     const endDate = new Date(params.endDate);
        //                     const diffTime = endDate - startDate;
        //                     const totalDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;

        //                     const totalDaysperHour = totalDays * 15;
        //                     const totalHours = HOURS.length;
        //                     const displayedMap = row._displayedMap?.hours || {};
        //                     const oosCount = Object.values(displayedMap).filter(v => v !== "Blank").length;
        //                     const formula = `${oosCount} / ${totalDaysperHour} * 100`;

        //                     const pctText = `${pct.toFixed(2)}%`;
        //                     const toolTipResult = `${pct}%`;

        //                     const tooltipText = `No. of OOS: ${oosCount}\nNo. of Hours: ${totalDaysperHour}\nFormula: ${formula}\nResult= ${toolTipResult}`;

        //                     return (
        //                         <Tooltip title={<span style={{ whiteSpace: "pre-line" }}>{tooltipText}</span>} placement="top">
        //                             <span>{pctText}</span>
        //                         </Tooltip>
        //                     );
        //                 },

        //                 customStyle: {
        //                     width: "6rem", textAlign: "center"
        //                 },
        //                 headerStyle: {
        //                     width: "6rem", textAlign: "center"
        //                 },
        //             },

        //             // Hourly columns
        //             ...HOURS.map((h, hourIndex) => {
        //                 const hourKey = moment(h, "h:mm A").format("HH:mm");

        //                 return {
        //                     header: formatHourDisplay(h),
        //                     accessorFn: (row) => row._displayedMap?.hours?.[hourKey] || null,
        //                     cell: (info) => {
        //                         const value = info.getValue();
        //                         const row = info.row.original;
        //                         const hourKey = moment(h, "h:mm A").format("HH:mm");

        //                         const salesVal = row[hourKey];

        //                         const isEmpty = (v) =>
        //                             v === null || v === undefined || v === 0 || v === "" || v === "0";

        //                         return (
        //                             <Tooltip
        //                                 title={isEmpty(salesVal) ? "No Sales" : `Sales: ₱${salesVal}`}
        //                                 placement="top"
        //                             >
        //                                 <div style={{ display: "flex", justifyContent: "center" }}>
        //                                     {value === "ClosedIcon" ? (
        //                                         <CloseRoundedIcon sx={{ fontSize: "small", color: "red" }} />
        //                                     ) : (
        //                                         <div>&nbsp;</div>
        //                                     )}
        //                                 </div>
        //                             </Tooltip>
        //                         );
        //                     },
        //                     customStyle: {
        //                         width: "5rem",
        //                         minWidth: "5rem",
        //                         maxWidth: "5rem",
        //                         textAlign: "center",
        //                         ...(h === "10:00 PM" && { borderRight: thickBorder }),
        //                     },
        //                     headerStyle: {
        //                         width: "5rem",
        //                         minWidth: "5rem",
        //                         maxWidth: "5rem",
        //                         textAlign: "center",
        //                         ...(h === "10:00 PM" && { borderRight: thickBorder }),
        //                     },
        //                 };
        //             }),
        //         ]
        //     }
        // ];

    }, [params?.days, params?.startDate, params?.endDate]);


    const stickyColumnIds = ["article_number", "article_description"];

    if (isLoading || !data || !params?.startDate && !params?.endDate || !processedData) {
        return <LoadingComponent />;
    }

    return (
        <TableComponentV2
            columns={fresh_hourly_oos_columns}
            data={processedData}
            hasPagination={true}
            isInventoryTable={true}
            isCompact={true}
            customTableHeaderStyle={{ backgroundColor: "#caddeb", textAlign: "center", height: "3em" }}
            customTableContainerStyle={{ borderRadius: 0, boxShadow: "none", overflowY: "auto" }}
            columnSticky={stickyColumnIds}
            propPageSize={20}
            rowsPerPageOptions={[20, 50, { label: 'All', value: data?.length }]}
            tableId={'overview_fresh_hourly_oos'}
        />
    );
}